/**
 * Ventana del alta integrada de Meta, **solo Instagram** (11.2, G532). WhatsApp ya no usa la ventana de Meta: su alta es el
 * enlace de YCloud (`logica/altaYCloud.ts`, G694, G695). La abre el SDK de JavaScript de Meta con los datos de `meta_signup`
 * que da `startChannelConnection` (`app_id`, `config_id`); la ventana devuelve el código de autorización, que el panel pasa
 * a `completeChannelConnection` con el `state`. El panel no guarda nada de esto.
 *
 * Las pruebas cambian `altaMeta.abrir` para simular la vuelta sin salir a la red.
 */
export interface DatosVentanaMeta {
    app_id: string;
    config_id: string;
}

export type VueltaMeta =
    /** Terminó: el código de autorización. */
    | { tipo: 'hecha'; codigo: string }
    /** La persona cerró la ventana sin terminar. */
    | { tipo: 'cerrada' }
    /** No se ha podido abrir la ventana de Meta (sin conexión con Meta o sin los datos de la aplicación). */
    | { tipo: 'sin-ventana' };

interface RespuestaLogin {
    authResponse?: { code?: string } | null;
}

interface SdkMeta {
    init: (opciones: Record<string, unknown>) => void;
    login: (alVolver: (r: RespuestaLogin) => void, opciones: Record<string, unknown>) => void;
}

const SDK = 'https://connect.facebook.net/es_ES/sdk.js';
/** Versión de la API Graph del SDK, la misma que usa el servidor por defecto (`canales.meta.version`). */
const VERSION_GRAPH = 'v21.0';

let cargando: Promise<SdkMeta | null> | null = null;

function sdk(): Promise<SdkMeta | null> {
    const ya = (window as unknown as { FB?: SdkMeta }).FB;
    if (ya !== undefined) {
        return Promise.resolve(ya);
    }
    cargando ??= new Promise<SdkMeta | null>((listo) => {
        const guion = document.createElement('script');
        guion.src = SDK;
        guion.async = true;
        guion.crossOrigin = 'anonymous';
        guion.onload = () => {
            listo((window as unknown as { FB?: SdkMeta }).FB ?? null);
        };
        guion.onerror = () => {
            cargando = null;
            listo(null);
        };
        document.head.appendChild(guion);
    });
    return cargando;
}

async function abrirConSdk(datos: DatosVentanaMeta): Promise<VueltaMeta> {
    if (datos.app_id === '' || datos.config_id === '') {
        return { tipo: 'sin-ventana' };
    }
    const fb = await sdk();
    if (fb === null) {
        return { tipo: 'sin-ventana' };
    }
    fb.init({ appId: datos.app_id, autoLogAppEvents: true, xfbml: false, version: VERSION_GRAPH });
    const respuesta = await new Promise<RespuestaLogin>((listo) => {
        fb.login(listo, { config_id: datos.config_id, response_type: 'code', override_default_response_type: true });
    });
    const codigo = respuesta.authResponse?.code ?? '';
    return codigo === '' ? { tipo: 'cerrada' } : { tipo: 'hecha', codigo };
}

export const altaMeta = {
    /** Abre la ventana del alta integrada de Instagram y espera a que vuelva. */
    abrir: (datos: DatosVentanaMeta): Promise<VueltaMeta> => abrirConSdk(datos),
};
